// src/modules/app-views/products/pages/Ai/components/TypingText.jsx
import { useState, useRef, useEffect } from 'react';

/**
 * TypingText - Component that simulates typing effect like Claude web
 * Reveals text character by character with natural timing and auto-scroll
 */
const TypingText = ({
  text,
  speed = 12,
  onComplete,
  onProgress,
  className,
  style,
  scrollContainerRef,
  showCursor = true,
  as: Component = 'span',
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const textEndRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!text) {
      setDisplayedText('');
      setIsComplete(true);
      return;
    }

    setDisplayedText('');
    setIsComplete(false);
    let currentIndex = 0;

    // Variable speed for more natural feel
    const getNextDelay = char => {
      // Pause longer on punctuation
      if (['.', '!', '?'].includes(char)) return speed * 4;
      if ([',', ';', ':'].includes(char)) return speed * 2;
      if (char === '\n') return speed * 3;
      // Random variation for natural feel
      return speed + Math.random() * (speed * 0.5);
    };

    const typeNextChar = () => {
      if (currentIndex < text.length) {
        const nextChar = text[currentIndex];
        setDisplayedText(text.slice(0, currentIndex + 1));
        currentIndex++;
        onProgress?.(currentIndex / text.length);

        // Auto-scroll: scroll the container to keep text visible (instant for typing)
        if (scrollContainerRef?.current) {
          const container = scrollContainerRef.current;
          container.scrollTop = container.scrollHeight;
        }

        setTimeout(typeNextChar, getNextDelay(nextChar));
      } else {
        setIsComplete(true);
        onComplete?.();
      }
    };

    // Start typing with a small delay
    const startTimer = setTimeout(typeNextChar, 100);

    return () => clearTimeout(startTimer);
  }, [text, speed, onComplete, onProgress, scrollContainerRef]);

  // ─────────────────────────────────────────────────────────────────────────────
  // UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Component className={className} style={style}>
      {displayedText}
      {showCursor && !isComplete && <span className="typing-cursor">|</span>}
      <span ref={textEndRef} />
    </Component>
  );
};

export default TypingText;
